import { useCallback, useEffect, useState, useMemo } from 'react';
import { formatDate, parseDate, formatForInput } from '../../utils/dateUtils';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClinicalDataCard from './ClinicalDataCard';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { medicationAPI } from '../../services/api';
import PlatformService from '../../services/PlatformService';

const resolveMedicationId = (medication) => medication?.id_med ?? medication?.id ?? null;

const formatMedicationField = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }
  return String(value);
};

const formatMedicationDate = (value) => formatDate(value);

const formatDateForInput = (value) => formatForInput(value);

const validationSchema = Yup.object({
  medication_name: Yup.string().required('Required'),
  dosage: Yup.string(),
  frequency: Yup.string(),
  route: Yup.string(),
  start_date: Yup.date().nullable(),
  end_date: Yup.date().nullable().notRequired(),
  prescribing_doctor: Yup.string(),
  reason: Yup.string(),
  status: Yup.string(),
  notes: Yup.string(),
});

// Real-time subscriptions removed with Supabase migration
const useMedicationsSubscription = () => {
  // No-op for now
};

/**
 * High-fidelity Medication Report View Component
 * Provides a professional, hospital-grade preview of medication data.
 */
const MedicationReportView = ({ medication, patientName, age, gender, amka, phone, address, diagnosis }) => {
  const reportDate = formatDate(new Date());
  const startDateStr = formatDate(medication.start_date);
  const endDateStr = medication.end_date ? formatDate(medication.end_date) : (medication.start_date ? 'Ongoing' : 'Not Set');

  return (
    <Box sx={{ p: 1, color: '#333', fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' }}>
      {/* Hospital-Grade Header */}
      <Box sx={{ borderBottom: '2px solid #1976d2', pb: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 800, letterSpacing: '-0.5px' }}>
            MEDICATION RECORD
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
            Generated on: {reportDate}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            MySurgeon EMR
          </Typography>
          <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 700 }}>
            CLINICAL DOCUMENTATION SYSTEM
          </Typography>
        </Box>
      </Box>

      {/* Patient Identity Card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 800, mb: 1, display: 'block', textTransform: 'uppercase' }}>
          Patient Identity Context
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Patient Name</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{patientName || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Age</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{age || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Gender</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, textTransform: 'capitalize' }}>{gender || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>AMKA / Social ID</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{amka || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Contact Info</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{phone || '—'}<br/>{address || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Primary Diagnosis</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{diagnosis || '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Medication Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 800, mb: 2, textTransform: 'uppercase', borderBottom: '1px solid #eee', pb: 0.5 }}>
          Prescription Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Medication Name</Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#d32f2f' }}>{medication.medication_name || medication.med_name || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Dosage</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{medication.dosage || '—'}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Frequency</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{medication.frequency || '—'}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Route</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{medication.route || '—'}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Start Date</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{startDateStr}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>End Date</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{endDateStr}</Typography>
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Status</Typography>
              <Chip 
                label={medication.status || 'Active'} 
                size="small" 
                color={medication.status === 'active' ? 'success' : 'default'} 
                sx={{ height: 20, fontSize: '11px', fontWeight: 700 }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Prescribing Physician</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{medication.prescribing_doctor || '—'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Reason for Prescription</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{medication.reason || '—'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Clinical Notes</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{medication.notes || '—'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Footer Info */}
      <Box sx={{ pt: 2, borderTop: '1px dashed #e0e0e0', mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#aaa' }}>
          Patient ID: {medication.id_pt} | Internal ID: {medication.id || medication.id_med} | Confidential Clinical Document
        </Typography>
      </Box>
    </Box>
  );
};

const MedicationsTab = ({ 
  patientId, 
  apiClient, 
  patientName = '', 
  age = '', 
  gender = '', 
  amka = '', 
  phone = '', 
  address = '', 
  diagnosis = '' 
}) => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('start_date');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('clinicalViewMode') || 'card');

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('clinicalViewMode', newMode);
    }
  };

  const loadMedications = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setError('');

    try {
      const response = await medicationAPI.getByPatientId(patientId);
      setMedications(response.data);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load medications');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadMedications();
  }, [loadMedications]);

  useMedicationsSubscription(patientId, loadMedications);

  const handleAdd = () => {
    setEditingMedication(null);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleEdit = (medication) => {
    setEditingMedication(medication);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (medication) => {
    setEditingMedication(medication);
    setViewOnly(true);
    setDialogOpen(true);
  };

  const handlePrint = (medication) => {
    const reportDate = formatDate(new Date());
    const startDateStr = formatDate(medication.start_date);
    const endDateStr = medication.end_date ? formatDate(medication.end_date) : (medication.start_date ? 'Ongoing' : 'Not Set');

    const reportHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Medication Report - ${patientName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body { font-family: 'Inter', sans-serif; line-height: 1.5; color: #333; padding: 40px; }
          .header { border-bottom: 2px solid #1976d2; padding-bottom: 15px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
          .hospital-name { font-size: 24px; font-weight: 800; color: #1976d2; margin: 0; }
          .identity-card { background: #f8f9fa; border: 1px solid #e0e0e0; padding: 20px; border-radius: 8px; margin-bottom: 30px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .field-label { font-size: 10px; font-weight: 800; color: #777; text-transform: uppercase; margin-bottom: 4px; }
          .field-value { font-size: 14px; font-weight: 600; }
          .section-title { font-size: 12px; font-weight: 800; color: #1976d2; text-transform: uppercase; border-bottom: 1px solid #1976d2; padding-bottom: 5px; margin-bottom: 15px; margin-top: 30px; }
          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
          .full-row { grid-column: span 2; }
          .status-tag { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 11px; font-weight: 800; text-transform: uppercase; background: #eee; }
          .status-active { background: #e8f5e9; color: #2e7d32; }
          .med-name-highlight { font-size: 18px; font-weight: 800; color: #d32f2f; }
          @media print {
            body { padding: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 class="hospital-name">MEDICATION RECORD</h1>
            <div style="font-size: 12px; color: #666; font-weight: 600;">Generated on: ${reportDate}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 18px; font-weight: 700;">MySurgeon EMR</div>
            <div style="font-size: 10px; font-weight: 700; color: #1976d2; text-transform: uppercase;">Clinical Documentation System</div>
          </div>
        </div>

        <div class="identity-card">
          <div>
            <div class="field-label">Patient Name</div>
            <div class="field-value">${patientName || '—'}</div>
          </div>
          <div>
            <div class="field-label">Age / Gender</div>
            <div class="field-value">${age || '—'} / ${gender || '—'}</div>
          </div>
          <div>
            <div class="field-label">AMKA / Social ID</div>
            <div class="field-value">${amka || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Primary Diagnosis</div>
            <div class="field-value">${diagnosis || '—'}</div>
          </div>
        </div>

        <div class="section-title">Prescription Informatics</div>
        <div class="details-grid">
          <div>
            <div class="field-label">Medication Name</div>
            <div class="med-name-highlight">${medication.medication_name || medication.med_name || '—'}</div>
          </div>
          <div>
            <div class="field-label">Current Status</div>
            <div class="status-tag ${medication.status === 'active' ? 'status-active' : ''}">${medication.status || 'Active'}</div>
          </div>
          <div>
            <div class="field-label">Dosage & Frequency</div>
            <div class="field-value">${medication.dosage || '—'} ${medication.frequency ? `— ${medication.frequency}` : ''}</div>
          </div>
          <div>
            <div class="field-label">Route of Administration</div>
            <div class="field-value">${medication.route || '—'}</div>
          </div>
          <div>
            <div class="field-label">Prescription Timeline</div>
            <div class="field-value">${medication.start_date || medication.end_date ? `Start: ${startDateStr} | End: ${endDateStr}` : 'No timeline specified'}</div>
          </div>
          <div>
            <div class="field-label">Prescribing Physician</div>
            <div class="field-value">${medication.prescribing_doctor || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Clinical Indication (Reason)</div>
            <div class="field-value" style="white-space: pre-wrap;">${medication.reason || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Clinical Notes</div>
            <div class="field-value" style="white-space: pre-wrap;">${medication.notes || '—'}</div>
          </div>
        </div>

        <div style="margin-top: 60px; border-top: 1px dashed #ccc; padding-top: 20px; text-align: center; font-size: 10px; color: #aaa;">
          Confidential Medical Record | Patient ID: ${medication.id_pt} | MySurgeon EMR Internal Report
        </div>
      </body>
      </html>
    `;

    const fileName = `${patientName || 'Patient'}_Medication_${medication.medication_name || medication.med_name || 'Record'}`;
    PlatformService.print(reportHtml, fileName);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this medication?')) return;

    try {
      await medicationAPI.delete(id);
      setMedications((prev) => prev.filter((m) => resolveMedicationId(m) !== id));
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete medication');
    }
  };

  const handleSubmit = async (values, { setStatus }) => {
    setSubmitting(true);
    setStatus(undefined);

    try {
      const payload = {
        id_pt: patientId,
        ...values,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
      };

      const medicationId = resolveMedicationId(editingMedication);

      if (editingMedication && medicationId) {
        const response = await medicationAPI.update(medicationId, payload);
        const updated = response.data;
        const updatedId = resolveMedicationId(updated);
        setMedications((prev) => prev.map((m) => (resolveMedicationId(m) === updatedId ? updated : m)));
      } else {
        const response = await medicationAPI.create(payload);
        const created = response.data;
        setMedications((prev) => [created, ...prev]);
      }

      setDialogOpen(false);
      setEditingMedication(null);
    } catch (err) {
      setStatus(err.response?.data?.error ?? err.message ?? 'Failed to save medication');
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = editingMedication
    ? {
        medication_name: editingMedication.medication_name ?? editingMedication.med_name ?? '',
        dosage: editingMedication.dosage ?? '',
        frequency: editingMedication.frequency ?? '',
        route: editingMedication.route ?? '',
        start_date: formatDateForInput(editingMedication.start_date),
        end_date: formatDateForInput(editingMedication.end_date),
        prescribing_doctor: editingMedication.prescribing_doctor ?? '',
        reason: editingMedication.reason ?? '',
        status: editingMedication.status ?? 'active',
        notes: editingMedication.notes ?? '',
      }
    : {
        medication_name: '',
        dosage: '',
        frequency: '',
        route: '',
        start_date: '',
        end_date: '',
        prescribing_doctor: '',
        reason: '',
        status: 'active',
        notes: '',
      };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedMedications = useMemo(() => {
    return [...medications].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'start_date') {
        aValue = parseDate(aValue)?.getTime() || 0;
        bValue = parseDate(bValue)?.getTime() || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [medications, order, orderBy]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Medications</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-med-label">Sort By</InputLabel>
            <Select
              labelId="sort-med-label"
              value={orderBy}
              label="Sort By"
              onChange={(e) => setOrderBy(e.target.value)}
            >
              <MenuItem value="start_date">Start Date</MenuItem>
              <MenuItem value="med_name">Name</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${order === 'asc' ? 'Descending' : 'Ascending'}`}>
            <IconButton onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')} color="primary">
              <TableSortLabel active direction={order} hideSortIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh medications">
            <IconButton onClick={loadMedications} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            color="primary"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Medication
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {medications.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No medications recorded yet
          </Typography>
        </Paper>
      ) : viewMode === 'card' ? (
        <Grid container spacing={2}>
          {sortedMedications.map((medication, index) => {
            const medicationId = resolveMedicationId(medication) ?? index;
            const medicationName = medication.medication_name ?? medication.med_name;
            const status = medication.status || 'Active';
            
            return (
              <Grid item xs={12} sm={6} md={4} key={medicationId}>
                <ClinicalDataCard
                  title={medicationName}
                  subtitle={medication.prescribing_doctor ? `Dr. ${medication.prescribing_doctor}` : null}
                  date={medication.start_date ? `${formatMedicationDate(medication.start_date)}${medication.end_date ? ` - ${formatMedicationDate(medication.end_date)}` : ' (Ongoing)'}` : (medication.end_date ? `Until ${formatMedicationDate(medication.end_date)}` : 'No Date')}
                  type="medication"
                  status={status}
                  statusColor={status === 'active' ? 'success' : status === 'discontinued' ? 'error' : 'default'}
                  content={[
                    { label: 'Dosage', value: `${medication.dosage || ''} ${medication.frequency || ''}`.trim() || null },
                    { label: 'Route', value: medication.route },
                    { label: 'Reason', value: medication.reason },
                  ]}
                  onEdit={() => handleEdit(medication)}
                  onDelete={() => handleDelete(medicationId)}
                  onPreview={() => handleView(medication)}
                  onPrint={() => handlePrint(medication)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', py: 1.5 }}>
                  <TableSortLabel
                    active={orderBy === 'start_date'}
                    direction={orderBy === 'start_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('start_date')}
                  >
                    START DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                  <TableSortLabel
                    active={orderBy === 'medication_name'}
                    direction={orderBy === 'medication_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('medication_name')}
                  >
                    DRUG NAME
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>DOSAGE / FREQ</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>END DATE</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', textAlign: 'center' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMedications.map((medication, index) => {
                const medicationId = resolveMedicationId(medication) ?? index;
                const status = medication.status || 'Active';
                return (
                  <TableRow key={medicationId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{formatMedicationDate(medication.start_date)}</TableCell>
                    <TableCell>{medication.medication_name ?? medication.med_name ?? '—'}</TableCell>
                    <TableCell>{`${medication.dosage || ''} ${medication.frequency || ''}`.trim() || '—'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={status} 
                        size="small" 
                        color={status === 'active' ? 'success' : status === 'discontinued' ? 'error' : 'default'} 
                        sx={{ height: 20, fontSize: '11px', fontWeight: 700, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatMedicationDate(medication.end_date)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handleView(medication)} color="primary">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton size="small" onClick={() => handlePrint(medication)} color="info">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(medication)} color="secondary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(medicationId)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, bgcolor: viewOnly ? '#f8f9fa' : 'transparent' }}>
          {viewOnly ? 'Prescription Preview' : editingMedication ? 'Edit Medication' : 'Add Medication'}
        </DialogTitle>
        
        {viewOnly ? (
          <>
            <DialogContent dividers>
              <MedicationReportView 
                medication={editingMedication}
                patientName={patientName}
                age={age}
                gender={gender}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
              <Button onClick={() => setDialogOpen(false)} variant="outlined">Close</Button>
              <Button 
                variant="contained" 
                startIcon={<PrintIcon />} 
                onClick={() => handlePrint(editingMedication)}
              >
                Print Report
              </Button>
            </DialogActions>
          </>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, touched, errors, handleBlur, status }) => (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        name="medication_name"
                        label="Medication Name"
                        value={values.medication_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.medication_name && Boolean(errors.medication_name)}
                        helperText={touched.medication_name && errors.medication_name}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="dosage"
                        label="Dosage"
                        value={values.dosage}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="frequency"
                        label="Frequency"
                        value={values.frequency}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="route"
                        label="Route"
                        value={values.route}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="status"
                        label="Status"
                        select
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="discontinued">Discontinued</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="start_date"
                        label="Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={values.start_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="end_date"
                        label="End Date (Optional)"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={values.end_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        helperText="Leave empty for ongoing medication"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="prescribing_doctor"
                        label="Prescribing Doctor"
                        value={values.prescribing_doctor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="reason"
                        label="Reason"
                        value={values.reason}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="notes"
                        label="Notes"
                        value={values.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                  {status && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {status}
                    </Alert>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                  <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </Box>
  );
};

export default MedicationsTab;
